/**
 * Field visibility for fill counts — mirrors {@link SimplePermissionWrapper} / {@link PermissionAwareField}.
 */

import { fieldMappingService } from '../config/fieldMapping';
import { fieldPermissionService } from '../services/auth/fieldPermissionService';
import { AppRole } from '../types/auth';

import type { SubsectionFieldDescriptor } from './subsectionHeaderMetrics';

export type FormFieldVisibilityContext = {
  showFullDetails?: boolean;
  requestStage?: string | null;
  requestId?: string | null;
  userRoles?: readonly AppRole[];
  userEmail?: string;
  partnerContributorAccessLevel?: 'read' | 'read_write';
};

export function normalizePermissionStage(stage?: string | null): string {
  const value = stage ?? '';
  if (value === 'Initial' || value === 'New') {
    return 'DRAFT';
  }
  if (value === 'CONFIGURATION') {
    return 'CONFIG';
  }
  return value || 'DRAFT';
}

/**
 * Basic view uses Requestor permissions at the **current** request stage.
 * Legacy behavior forced DRAFT at post-draft stages, hiding Site Assessment fields (#997 Bug 1).
 */
export function resolveBasicViewPermissionStage(
  requestStage?: string | null,
): string {
  return normalizePermissionStage(requestStage);
}

/**
 * View-mode role filter (composable, #997 — Story 3.1 D3).
 *
 * The Basic/Advanced toggle substitutes the REQUESTOR role for a non-partner
 * user in Basic view. This is the ONLY place that substitution lives, factored
 * out of {@link resolveEffectiveFormViewPermissionContext} so the RBAC filter
 * ({@link checkRbacFieldVisibility}) stays independent of the toggle. The
 * descriptor engine deliberately does NOT call this — it composes view-mode and
 * RBAC as separate inputs (see {@link isDescriptorFieldVisible}) so the toggle
 * can be removed cleanly later. Behavior-preserving for existing callers.
 */
export function filterRolesForViewMode(
  showFullDetails: boolean | undefined,
  userRoles: readonly AppRole[] | undefined,
): AppRole[] {
  const isBasicViewMode = showFullDetails === false;
  const isPartnerContributor = (userRoles ?? []).includes(
    AppRole.PARTNER_CONTRIBUTOR,
  );
  if (isBasicViewMode && !isPartnerContributor) {
    return [AppRole.REQUESTOR];
  }
  return (userRoles ?? []) as AppRole[];
}

/**
 * Effective permission stage for a form view. Basic and Advanced both resolve
 * to `normalizePermissionStage(requestStage)` today (the legacy DRAFT-forcing
 * was removed in #997 Bug 1); kept as a named helper so callers read intent.
 */
export function resolveViewPermissionStage(
  requestStage: string | null | undefined,
): string {
  return normalizePermissionStage(requestStage);
}

export function resolveEffectiveFormViewPermissionContext(
  context: FormFieldVisibilityContext,
): {
  effectiveStage: string;
  effectiveRoles: AppRole[];
  requestId?: string;
  userEmail?: string;
} {
  return {
    effectiveStage: resolveViewPermissionStage(context.requestStage),
    effectiveRoles: filterRolesForViewMode(
      context.showFullDetails,
      context.userRoles,
    ),
    requestId: context.requestId ?? undefined,
    userEmail: context.userEmail,
    partnerContributorAccessLevel: context.partnerContributorAccessLevel,
  };
}

/**
 * Pure RBAC visibility check (composable). Given an already-resolved role set +
 * stage, returns whether the field is readable and not hidden. Carries NO
 * view-mode logic — the caller decides which roles to pass (live callers via
 * {@link filterRolesForViewMode}; the descriptor engine via the user's actual
 * roles). Assumes the stage cache is warmed.
 */
export function checkRbacFieldVisibility(params: {
  roles: readonly AppRole[];
  section: string;
  field: string;
  stage: string;
  requestId?: string;
  userEmail?: string;
  partnerContributorAccessLevel?: 'read' | 'read_write';
}): boolean {
  if (params.roles.length === 0) {
    return false;
  }
  const access = fieldPermissionService.checkFieldAccessSync({
    userRoles: params.roles as AppRole[],
    section: params.section,
    field: params.field,
    requestStage: params.stage,
    requestId: params.requestId,
    userEmail: params.userEmail,
    partnerContributorAccessLevel: params.partnerContributorAccessLevel,
  });
  return !access.isHidden && access.canRead;
}

export function isSummaryFieldMappingVisibleInView(
  mapping: { permissionSection: string; permissionField: string },
  context: FormFieldVisibilityContext,
): boolean {
  const {
    effectiveStage,
    effectiveRoles,
    requestId,
    userEmail,
    partnerContributorAccessLevel,
  } = resolveEffectiveFormViewPermissionContext(context);

  fieldPermissionService.warmStagePermissionsCacheSync(effectiveStage);

  return checkRbacFieldVisibility({
    roles: effectiveRoles,
    section: mapping.permissionSection,
    field: mapping.permissionField,
    stage: effectiveStage,
    requestId,
    userEmail,
    partnerContributorAccessLevel,
  });
}

/**
 * Decoupled field-visibility for the descriptor engine (#997 — Story 3.1 D3).
 *
 * Composes the two independent filters explicitly: an optional view-mode gate
 * that the descriptor owns, then the pure RBAC check using the user's ACTUAL
 * roles. It never performs the `showFullDetails === false` forces-REQUESTOR
 * conflation that {@link filterRolesForViewMode} keeps alive for live sections,
 * so removing the Basic/Advanced toggle later touches only the view-mode gate.
 */
export interface DescriptorFieldVisibilityInput {
  viewMode: 'basic' | 'full';
  roles: readonly AppRole[];
  requestStage?: string | null;
  requestId?: string;
  userEmail?: string;
}

export function isDescriptorFieldVisible(
  mapping: { permissionSection: string; permissionField: string },
  input: DescriptorFieldVisibilityInput,
  hiddenInBasicView?: (mapping: {
    permissionSection: string;
    permissionField: string;
  }) => boolean,
): boolean {
  // View-mode filter — independent of RBAC; the descriptor decides what Basic
  // view hides, WITHOUT substituting the REQUESTOR role.
  if (input.viewMode === 'basic' && hiddenInBasicView?.(mapping)) {
    return false;
  }

  // RBAC filter — independent of view-mode; uses the user's actual roles.
  const stage = resolveViewPermissionStage(input.requestStage);
  fieldPermissionService.warmStagePermissionsCacheSync(stage);
  return checkRbacFieldVisibility({
    roles: input.roles,
    section: mapping.permissionSection,
    field: mapping.permissionField,
    stage,
    requestId: input.requestId,
    userEmail: input.userEmail,
  });
}

export function isFormFieldKeyVisibleInView(
  formFieldKey: string,
  context: FormFieldVisibilityContext,
): boolean {
  const mapping = fieldMappingService.getMappingByFormField(formFieldKey);
  if (!mapping) {
    return context.showFullDetails !== false;
  }

  return isSummaryFieldMappingVisibleInView(
    {
      permissionSection: mapping.jsonSection,
      permissionField: mapping.jsonField,
    },
    context,
  );
}

export function filterSubsectionFieldsForFormView(
  fields: readonly SubsectionFieldDescriptor[],
  context?: FormFieldVisibilityContext,
): readonly SubsectionFieldDescriptor[] {
  if (!context) {
    return fields;
  }

  return fields.filter(field =>
    isFormFieldKeyVisibleInView(field.key, context),
  );
}

export function filterSummaryFieldMappingsForFormView<
  T extends {
    dataField: string;
    permissionSection: string;
    permissionField: string;
  },
>(mappings: readonly T[], context?: FormFieldVisibilityContext): readonly T[] {
  if (!context) {
    return mappings;
  }

  return mappings.filter(mapping =>
    isSummaryFieldMappingVisibleInView(
      {
        permissionSection: mapping.permissionSection,
        permissionField: mapping.permissionField,
      },
      context,
    ),
  );
}
